import {
  type Interest,
  type PublicProfile,
  type SuggestedUser,
  interests as interestsApi,
  users
} from '@/lib/api';
import { useDebounce } from '@/lib/hooks/use-debounce';
import { routes } from '@/lib/routes';
import { motion } from 'framer-motion';
import { Loader2, MapPin, PenLine, Search, Sparkles, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

export default function DiscoverPage() {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<SuggestedUser[]>([]);
  const [searchResults, setSearchResults] = useState<PublicProfile[]>([]);
  const [interests, setInterests] = useState<Record<string, Interest[]>>({});
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  const [query, setQuery] = useState('');
  const [selectedInterest, setSelectedInterest] = useState<string | null>(null);
  const debouncedQuery = useDebounce(query, 300);

  // Load initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [suggestionsData, interestsData] = await Promise.all([
          users.getSuggested(10),
          interestsApi.getGrouped()
        ]);
        setSuggestions(suggestionsData ?? []);
        setInterests(interestsData ?? {});
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setSuggestions([]);
        setInterests({});
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Search users
  useEffect(() => {
    if (!debouncedQuery && !selectedInterest) {
      setSearchResults([]);
      return;
    }

    const search = async () => {
      setSearching(true);
      try {
        let results: PublicProfile[];
        if (selectedInterest) {
          results = await users.getByInterest(selectedInterest);
        } else {
          results = await users.search(debouncedQuery);
        }
        setSearchResults(results ?? []);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };

    search();
  }, [debouncedQuery, selectedInterest]);

  const handleInterestClick = (interestId: string) => {
    if (selectedInterest === interestId) {
      setSelectedInterest(null);
    } else {
      setSelectedInterest(interestId);
      setQuery('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-sepia animate-spin" />
      </div>
    );
  }

  const showResults = debouncedQuery || selectedInterest;
  const displayUsers = showResults ? searchResults : suggestions;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-serif text-ink">{t('discover.title')}</h1>
        <p className="text-sepia/70">{t('discover.subtitle')}</p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-warm" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedInterest(null);
            }}
            placeholder={t('discover.searchPlaceholder')}
            className="w-full pl-12 pr-4 py-3 border border-gray-warm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all bg-white"
            style={{ boxShadow: 'var(--shadow-sm)' }}
          />
          {searching && (
            <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-sepia animate-spin" />
          )}
        </div>
      </div>

      {/* Interest filters */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-sepia mb-3">{t('discover.browseByInterest')}</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(interests)
            .slice(0, 3)
            .flatMap(([, categoryInterests]) => categoryInterests.slice(0, 4))
            .map((interest) => (
              <button
                type="button"
                key={interest.id}
                onClick={() => handleInterestClick(interest.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedInterest === interest.id
                    ? 'bg-blue text-white'
                    : 'bg-gray-warm/30 text-sepia hover:bg-gray-warm/50'
                }`}
              >
                {interest.name}
              </button>
            ))}
        </div>
      </div>

      {/* Results header */}
      <div className="flex items-center gap-2 mb-4">
        {showResults ? (
          <>
            <Users className="w-5 h-5 text-sepia" />
            <h2 className="text-lg font-medium text-ink">
              {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'}
            </h2>
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5 text-gold" />
            <h2 className="text-lg font-medium text-ink">{t('discover.suggestedPals')}</h2>
          </>
        )}
      </div>

      {/* User grid */}
      {displayUsers.length === 0 ? (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-warm/30 mb-4">
            <Users className="w-8 h-8 text-sepia/50" />
          </div>
          <h3 className="text-lg font-medium text-ink mb-2">
            {showResults ? t('common.noResults') : t('discover.suggestedDesc')}
          </h3>
          <p className="text-sepia/70">
            {showResults ? t('discover.searchPlaceholder') : t('discover.suggestedDesc')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {displayUsers.map((user, index) => (
            <UserCard
              key={user.id}
              user={user}
              index={index}
              sharedInterests={
                'sharedInterests' in user ? (user as SuggestedUser).sharedInterests : undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UserCard({
  user,
  index,
  sharedInterests
}: {
  user: PublicProfile;
  index: number;
  sharedInterests?: number;
}) {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link to={routes.user(user.username)}>
        <div
          className="bg-white rounded-lg p-5 hover:translate-y-[-2px] transition-all cursor-pointer"
          style={{ boxShadow: 'var(--shadow-md)' }}
        >
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="w-12 h-12 rounded-full bg-gray-warm/50 flex items-center justify-center text-lg font-medium text-sepia shrink-0">
              {user.username.charAt(0).toUpperCase()}
            </div>

            <div className="flex-1 min-w-0">
              {/* Name and country */}
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-ink truncate">{user.username}</h3>
                <span className="flex items-center gap-1 text-xs text-sepia/60">
                  <MapPin className="w-3 h-3" />
                  {user.country}
                </span>
              </div>

              {/* Bio preview */}
              {user.bio && <p className="text-sm text-sepia/70 line-clamp-2 mb-2">{user.bio}</p>}

              {/* Interests */}
              <div className="flex flex-wrap gap-1.5">
                {(user.interests ?? []).slice(0, 3).map((interest) => (
                  <span
                    key={interest.id}
                    className="px-2 py-0.5 bg-gray-warm/30 text-sepia text-xs rounded-full"
                  >
                    {interest.name}
                  </span>
                ))}
                {(user.interests?.length ?? 0) > 3 && (
                  <span className="px-2 py-0.5 text-sepia/60 text-xs">
                    +{(user.interests?.length ?? 0) - 3} more
                  </span>
                )}
              </div>

              {/* Shared interests badge */}
              {sharedInterests !== undefined && sharedInterests > 0 && (
                <div className="mt-2 flex items-center gap-1 text-xs text-blue">
                  <Sparkles className="w-3.5 h-3.5" />
                  {t('discover.sharedInterests', { count: sharedInterests })}
                </div>
              )}
            </div>

            {/* Write button */}
            <Link
              to={routes.composeToUser(user.id)}
              onClick={(e) => e.stopPropagation()}
              className="p-2 rounded-full hover:bg-gray-warm/30 transition-all"
              title={t('discover.writeTo', { name: user.username })}
            >
              <PenLine className="w-5 h-5 text-gold" />
            </Link>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
